import { platformBrowserDynamic } from '@angular/platform-browser-dynamic';
import { AppModule } from './app/app.module';
import * as Sentry from '@sentry/angular';
import { environment } from './environments/environment';

import { UserJourneyEvent } from './app/core/services/user-journey.types';

// Temporary: avoid noisy Sentry requests on local/dev hosts to keep console usable
const isLocalHost = ['localhost', '127.0.0.1'].includes(window.location.hostname);

if (environment.production && environment.sentryDsn && !isLocalHost) {
Sentry.init({
    dsn: environment.sentryDsn,
    environment: environment.envName, // Добавляем environment tag в Sentry
    integrations: [
        Sentry.browserTracingIntegration(),
        Sentry.replayIntegration()
    ],
    // Performance Monitoring
    tracesSampleRate: 0.05, 
    // Set 'tracePropagationTargets' to control for which URLs distributed tracing should be enabled
    tracePropagationTargets: ['localhost', /^https:\/\/api.map.perare\.io\/api/, /^https:\/\/api.dorado.dev.perare\.io\/api/],
    // Session Replay
    replaysSessionSampleRate: 0.01, 
    replaysOnErrorSampleRate: 0.1,
    beforeSend(event, hint) {
      // Инициализируем теги, если их нет
      if (!event.tags) {
        event.tags = {};
      }

      // --- Фильтр: Игнорируем ошибки загрузки SVG ---
      // Эти ошибки возникают при прерывании загрузки SVG preloader'ом (HTTP 0)
      // Обычно когда пользователь покидает страницу до завершения загрузки
      // Иконки загрузятся по требованию, так что эти ошибки не критичны
      let error = hint.originalException as Error;
      let message = (error && error.message) || (event.message as string) || '';
      
      if (
        message.includes('Http failure response for') &&
        (message.includes('/assets/icons-preload/') || message.includes('/assets/custom-icons/')) &&
        message.includes('.svg')
      ) {
        return null; // Игнорируем эту ошибку
      }

      // --- Эвристика 1: Анализ трассировки стека ---
      if (error && error.stack) {
        const criticalStacks = [
          'payment.service.ts',
          'auth.service.ts',
          'purchase.service.ts',
          'ai-report.service.ts',
          'premium-layer.service.ts',
          'file-download.service.ts',
        ];
        if (criticalStacks.some(fragment => error.stack!.includes(fragment))) {
          event.tags['criticality'] = 'high';
          event.level = 'fatal';
        }
      }
      
      // --- Эвристика 2: Анализ действий пользователя (хлебных крошек) ---
      const breadcrumbs = event.breadcrumbs || [];
      const criticalJourneyEvents = [
        UserJourneyEvent.START_PAYMENT,
        UserJourneyEvent.PURCHASE_COMPLETE,
        UserJourneyEvent.PAYMENT_FAILED,
        UserJourneyEvent.SIGN_UP_COMPLETE,
        UserJourneyEvent.SELECT_PLAN,
        UserJourneyEvent.AI_REPORT_START,
        UserJourneyEvent.AI_REPORT_ERROR,
        UserJourneyEvent.AI_REPORT_CREDIT_FAILED,
        UserJourneyEvent.PLOT_DATA_PURCHASE_REQUEST,
        UserJourneyEvent.PLOT_DATA_PURCHASE_FAILURE,
        UserJourneyEvent.PLOT_ANALYTICS_PURCHASE_REQUEST,
        UserJourneyEvent.PLOT_ANALYTICS_PURCHASE_FAILURE,
        UserJourneyEvent.PREMIUM_LAYER_ERROR,
        UserJourneyEvent.FILE_DOWNLOAD_FAILURE,
        UserJourneyEvent.DOWNLOAD_SHP_FAILURE,
      ];

      const lastUserJourneyBreadcrumb = breadcrumbs
        .filter(b => b.category === 'user-journey')
        .slice(-5) // Анализируем последние 5 действий
        .find(b => criticalJourneyEvents.includes(b.message as UserJourneyEvent));

      if (lastUserJourneyBreadcrumb) {
        event.tags['criticality'] = 'high';
        event.tags['last_user_action'] = lastUserJourneyBreadcrumb.message;
        event.level = 'fatal';
      }

      // --- Эвристика 3: Анализ типа и текста ошибки ---
      if (!event.tags['criticality']) {
        const errorType = (error && (error as any).name) || (event.exception?.values?.[0]?.type as string) || '';
        message = (error && error.message) || (event.message as string) || '';

        const typeMatches = [
          'TypeError',
          'ReferenceError',
          'ZoneAwareError',
          'UnhandledPromiseRejection',
          'HttpErrorResponse',
          'NetworkError',
          'TimeoutError',
        ].some(t => errorType.includes(t));

        const keywordMatches = /(undefined|null|cannot read property|is not defined|Failed to fetch|Network request failed|timeout|connection timed out|Internal Server Error|Bad Gateway|Uncaught \(in promise\))/i.test(message);

        if (typeMatches || keywordMatches) {
          event.tags['criticality'] = 'medium';
          event.tags['auto_priority_rule'] = 'error_pattern';
          event.level = 'error';
        }
      }

      return event;
    },
});
}

platformBrowserDynamic()
  .bootstrapModule(AppModule)
  .catch((err) => console.error(err));
